import React, {useEffect, useId, useRef, useState} from 'react'
import {View} from 'react-native'
import {type AppBskyEmbedVideo} from '@atproto/api'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import type * as HlsTypes from 'hls.js'

import {atoms as a} from '#/alf'
import {MediaInsetBorder} from '#/components/MediaInsetBorder'

/**
 * Simplified video player that:
 * - Autoplays when visible
 * - Loops continuously
 * - Unmutes only when prominently visible (>50%)
 * - No controls (just video)
 */
export function VideoPlayer({
  embed,
}: {
  embed: AppBskyEmbedVideo.View
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isProminentlyVisible, setIsProminentlyVisible] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const figId = useId()
  const {_} = useLingui()

  // Throw error to error boundary
  if (error) {
    throw error
  }

  const hlsRef = useHLS({
    playlist: embed.playlist,
    setError,
    videoRef,
  })

  // Intersection Observer to detect when video is visible
  // Uses two thresholds: 10% for play/pause, 50% for audio
  useEffect(() => {
    if (!containerRef.current) return

    // Observer for basic visibility (play/pause)
    const visibilityObserver = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (!entry) return
        setIsVisible(entry.isIntersecting)
      },
      {threshold: 0.1}, // Trigger when at least 10% visible
    )

    // Observer for prominent visibility (audio on/off)
    const audioObserver = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (!entry) return
        setIsProminentlyVisible(entry.isIntersecting)
      },
      {threshold: 0.5}, // Trigger when at least 50% visible
    )

    visibilityObserver.observe(containerRef.current)
    audioObserver.observe(containerRef.current)

    return () => {
      visibilityObserver.disconnect()
      audioObserver.disconnect()
    }
  }, [])

  // Autoplay when visible
  useEffect(() => {
    if (!videoRef.current) return

    if (isVisible) {
      videoRef.current.play().catch(err => {
        console.log('[VideoPlayer] Autoplay failed:', err)
      })
    } else {
      videoRef.current.pause()
    }
  }, [isVisible])

  // Manage audio based on prominent visibility
  useEffect(() => {
    if (!videoRef.current) return

    // Unmute when prominently visible (>50%), mute otherwise
    videoRef.current.muted = !isProminentlyVisible
  }, [isProminentlyVisible])

  return (
    <View
      style={[a.flex_1, a.rounded_md, a.overflow_hidden]}
      accessibilityLabel={_(msg`Video`)}
      accessibilityHint="">
      <div ref={containerRef} style={{height: '100%', width: '100%'}}>
        <figure style={{margin: 0, position: 'absolute', inset: 0}}>
          <video
            ref={videoRef}
            poster={embed.thumbnail}
            style={{width: '100%', height: '100%', objectFit: 'cover'}}
            playsInline
            preload="auto"
            muted
            loop
            aria-labelledby={embed.alt ? figId : undefined}
          />
          {embed.alt && (
            <figcaption
              id={figId}
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                borderWidth: 0,
              }}>
              {embed.alt}
            </figcaption>
          )}
        </figure>
        <MediaInsetBorder />
      </div>
    </View>
  )
}

// HLS error classes
export class HLSUnsupportedError extends Error {
  constructor() {
    super('HLS is not supported')
  }
}

export class VideoNotFoundError extends Error {
  constructor() {
    super('Video not found')
  }
}

type CachedPromise<T> = Promise<T> & {value: undefined | T}
const promiseForHls = import(
  // @ts-ignore
  'hls.js/dist/hls.min'
).then(mod => mod.default) as CachedPromise<typeof HlsTypes.default>
promiseForHls.value = undefined
promiseForHls.then(Hls => {
  promiseForHls.value = Hls
})

function useHLS({
  playlist,
  setError,
  videoRef,
}: {
  playlist: string
  setError: (v: Error | null) => void
  videoRef: React.RefObject<HTMLVideoElement>
}) {
  const hlsRef = useRef<HlsTypes.default | undefined>(undefined)

  useEffect(() => {
    if (!videoRef.current) return

    async function loadHls() {
      if (!videoRef.current) return

      const Hls = await promiseForHls
      const video = videoRef.current

      // Check for native HLS support (Safari)
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = playlist
        return
      }

      // Use HLS.js for other browsers
      if (!Hls.isSupported()) {
        setError(new HLSUnsupportedError())
        return
      }

      const hls = new Hls({
        maxBufferLength: 10,
        maxMaxBufferLength: 30,
      })

      hlsRef.current = hls
      hls.attachMedia(video)

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(playlist)
      })

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('[VideoPlayer] Network error')
              setError(new VideoNotFoundError())
              break
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error('[VideoPlayer] Media error, attempting recovery')
              hls.recoverMediaError()
              break
            default:
              console.error('[VideoPlayer] Fatal error')
              setError(new Error('Video playback error'))
              break
          }
        }
      })
    }

    loadHls()

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = undefined
      }
    }
  }, [playlist, setError, videoRef])

  return hlsRef
}
